// Sprint Hub Design System Tokens
// ASCII only -- no unicode characters
import { createContext, useContext } from 'react';

// Font family variables
var FONT = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif";
var MONO = "'IBM Plex Mono', 'SF Mono', monospace";

// ---------------------------------------------------------------------------
// Theme tokens -- light and dark
// ---------------------------------------------------------------------------
var THEMES = {
  light: {
    bg: '#f8f7f4',
    surface: '#ffffff',
    surfaceRaised: '#ffffff',
    surfaceInset: '#f0efe9',
    text: '#12110f',
    textSecondary: '#6b6962',
    textMuted: '#8b8880',
    border: '#e5e3dc',
    borderSubtle: '#f0efe9',
    accent: '#4F6EF7',
    accentHover: '#3D5CE5',
    accentLight: '#EEF1FE',
    success: '#10B981',
    successLight: '#ECFDF5',
    warning: '#F59E0B',
    warningLight: '#FFFBEB',
    critical: '#EF4444',
    criticalLight: '#FEF2F2',
    cardShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    cardShadowHover: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)',
    sidebar: '#FFFFFF',
    sidebarBorder: '#e5e3dc',
    sidebarText: '#8b8880',
    sidebarTextActive: '#12110f',
    sidebarHover: '#f0efe9',
    sidebarActiveBg: '#EEF1FE',
    topbar: '#ffffff',
    // backward-compat aliases for existing components
    bgSecondary: '#f0efe9',
    bgTertiary: '#f0efe9',
    borderLight: '#f0efe9',
    card: '#ffffff',
    blue: '#4F6EF7',
    blueLight: '#EEF1FE',
    blueLt: '#EEF1FE',
    green: '#10B981',
    yellow: '#F59E0B',
    red: '#EF4444',
    sidebarActive: '#EEF1FE',
    sidebarIconBg: '#f0efe9',
    sidebarIconColor: '#8b8880',
    codeBlock: '#1F2937',
  },
  dark: {
    bg: '#12110f',
    surface: '#1c1b18',
    surfaceRaised: '#24231f',
    surfaceInset: '#24231f',
    text: '#e8e6e0',
    textSecondary: '#b0ada5',
    textMuted: '#7a7770',
    border: '#2e2d28',
    borderSubtle: '#24231f',
    accent: '#6B8AFF',
    accentHover: '#5A78F0',
    accentLight: '#1A2240',
    success: '#34D399',
    successLight: '#0D2922',
    warning: '#FBBF24',
    warningLight: '#2A2210',
    critical: '#F87171',
    criticalLight: '#2A1515',
    cardShadow: '0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.1)',
    cardShadowHover: '0 4px 12px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.15)',
    sidebar: '#1c1b18',
    sidebarBorder: '#2e2d28',
    sidebarText: '#7a7770',
    sidebarTextActive: '#e8e6e0',
    sidebarHover: 'rgba(255,255,255,0.06)',
    sidebarActiveBg: '#1A2240',
    topbar: '#1c1b18',
    // backward-compat aliases for existing components
    bgSecondary: '#24231f',
    bgTertiary: '#2e2d28',
    borderLight: '#24231f',
    card: '#1c1b18',
    blue: '#6B8AFF',
    blueLight: '#1A2240',
    blueLt: '#1A2240',
    green: '#34D399',
    yellow: '#FBBF24',
    red: '#F87171',
    sidebarActive: '#1A2240',
    sidebarIconBg: '#24231f',
    sidebarIconColor: '#b0ada5',
    codeBlock: '#12110f',
  }
};

export var ThemeContext = createContext(THEMES.light);
export var useTheme = function() { return useContext(ThemeContext); };

export var UiModeContext = createContext('standard');
export var useUiMode = function() { return useContext(UiModeContext); };

export var colors = {
  // Base
  background: '#f8f7f4',
  foreground: '#1a1a18',

  // Accent (blue -- the ONLY color accent)
  accent: '#4F6EF7',
  accentHover: '#3D5CE5',
  accentForeground: '#FFFFFF',

  // Muted
  mutedText: '#5e5e5a',
  mutedBg: '#f0efe8',
  mutedBgHover: '#d8d7d0',

  // Borders
  border: '#d8d7d0',

  // Status -- for indicators ONLY, never for UI chrome
  statusGreen: '#0F6E56',
  statusAmber: '#854F0B',
  statusRed: '#A32D2D',

  // Nav glassmorphism
  navBg: 'rgba(255, 255, 255, 0.80)',
  navBlur: 'blur(16px)',

  // Cards
  cardBg: '#FFFFFF',
  cardBorder: '#E8EAF0',
};

export var typography = {
  fontFamily: FONT,
  monoFamily: MONO,
  sizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
};

export var spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
};

export var radii = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',         // cards
  button: '0.75rem',  // buttons
  full: '9999px',
};

export var shadows = {
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.04)',
  lg: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04)',
  xl: '0 20px 25px -5px rgba(0,0,0,0.08), 0 10px 10px -5px rgba(0,0,0,0.03)',
};

export var layout = {
  sidebarWidth: '220px',
  topBarHeight: '56px',
  mobileBreakpoint: 768,
  agentPanelWidth: '380px',
  mobileTabBarHeight: '56px',
};

export var transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
};

// CSS custom property names for use in stylesheets
export var cssVars = {
  background: '--color-background',
  foreground: '--color-foreground',
  accent: '--color-accent',
  accentHover: '--color-accent-hover',
  accentFg: '--color-accent-fg',
  mutedText: '--color-muted-text',
  mutedBg: '--color-muted-bg',
  mutedBgHover: '--color-muted-bg-hover',
  border: '--color-border',
  statusGreen: '--color-status-green',
  statusAmber: '--color-status-amber',
  statusRed: '--color-status-red',
  navBg: '--color-nav-bg',
  sidebarWidth: '--layout-sidebar-width',
  topBarHeight: '--layout-topbar-height',
  tabBarHeight: '--layout-tabbar-height',
};

export { THEMES };
export { FONT, MONO };

export default {
  colors,
  typography,
  spacing,
  radii,
  shadows,
  layout,
  transitions,
  cssVars,
};
